import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete,Button,Dialog,DialogActions,DialogContent,DialogTitle,FormControl,InputLabel,MenuItem,Select,TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useMask } from '@react-input/mask';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from "react-toastify";
import AttToken from '../../../helpers/attToken';
import { trackEventMatomo } from '../../../helpers/matomo';
import Clients from "../../../service/Clients";
import queryClient from '../../../service/query';
import { ClientType } from "../../../types/clientes/types";
import { userTag } from '../../../types/users/types';
import Loading from '../../Loading/Loading';

export type TagType = {
  id: string,
  name: string,
};
const customerService = new Clients();

export default function ChangeCustomerModal({
  selectedCustomer,
  setSelectedCustomer,
  customerTags,
  openModal,
  setOpenModal,
}: {
  selectedCustomer: ClientType | null,
  setSelectedCustomer: Function,
  customerTags: userTag[],
  openModal: boolean,
  setOpenModal: Function,
}) {

  const inputRef = useMask({ mask: '(__) ____-_____', replacement: '_' });

  const { t } = useTranslation('translation');

  const { mutate, isPending } = useMutation({
    mutationKey: ['UPDATE_CUSTOMER'],
    mutationFn: async () => {
      try {
        delete selectedCustomer?.dataAlteracao;
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        const promise = toast.promise(async () => {
          const response = await customerService.updateClient({ ...token }, selectedCustomer as ClientType);
          if (!response || !response.dataAlteracao) throw new Error('No client updated');
          return response;
        }, {
          pending: t('toast.saving'),
          success: t('toast.savedSuccess'),
          error: t('toast.errorSaving'),
        }, { toastId: 'update' });
        return promise;
      }
      catch (err) {
        console.error(err);
        throw err;
      }
    },
    onSuccess: () => {
      trackEventMatomo('gestão de clientes', 'sucesso', 'edição', 'cliente atualizado');
      queryClient.resetQueries({ queryKey: ['GET_CUSTOMERS'] });
      setSelectedCustomer(null);
      setOpenModal(false);
    },
    onError: () => trackEventMatomo('gestão de clientes', 'erro', 'edição', 'falha ao atualizar cliente'),
  });

  return isPending ? (<Loading />) : (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={openModal}
      onClose={() => {
        trackEventMatomo('gestão de clientes', 'click', 'modal', 'fecha edição de cliente');
        setOpenModal(false);
      }}
      scroll={'paper'}
    >
      <DialogTitle id="scroll-dialog-title">{t('customerPage.AlterarCliente')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => {
          trackEventMatomo('gestão de clientes', 'click', 'botão', 'fecha edição de cliente');
          setOpenModal(false);
        }}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <TextField
          onClick={() => trackEventMatomo('modal edição cliente', 'click', 'input', 'editar valor')}
          style={{ marginBottom: '20px' }}
          label={t('name')}
          fullWidth
          inputProps={{ maxLength: 100 }}
          type="text"
          value={selectedCustomer?.nome}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedCustomer({ ...selectedCustomer as ClientType, nome: e.target.value })}
        />
        <TextField
          onClick={() => trackEventMatomo('modal edição cliente', 'click', 'input', 'editar valor')}
          style={{ marginBottom: '10px' }}
          label={t('name')}
          fullWidth
          type='tel'
          inputProps={{ maxLength: 100 }}
          inputRef={inputRef}
          value={selectedCustomer?.contato}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedCustomer({ ...selectedCustomer as ClientType, contato: e.target.value })}
        />
        <Autocomplete
          onClick={() => trackEventMatomo('modal edição cliente', 'click', 'select', 'seleciona valor')}
          style={{ marginBottom: '10px' }}
          multiple
          value={selectedCustomer?.tags?.map((ele) => (
            {
              value: ele || '',
              label: customerTags.find((elem) => elem.id === ele)?.name || ''
            }
          )) || undefined}
          id="combo-box-demo"
          sx={{ marginTop: '10px' }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          filterSelectedOptions
          noOptionsText={`${t('checklists.Modal.noAvailableTags')}`}
          options={
            customerTags
              ?.sort((a, b) => { return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1; })
              ?.map((ele) => ({
                label: ele.name,
                value: ele.id,
              })) || []}
          onChange={(_event, _newValue: { label: string, value: string }[] | null, reason, details) => {
            const value = details?.option.value;
            if (selectedCustomer?.tags?.includes(value as string)) {
              setSelectedCustomer({ ...selectedCustomer as ClientType, tags: selectedCustomer?.tags ? selectedCustomer?.tags?.filter((el) => el !== value) : [] });
            } else {
              setSelectedCustomer({ ...selectedCustomer as ClientType, tags: selectedCustomer?.tags ? [...selectedCustomer?.tags, value] : [value] });
            }
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.value}>
              <span>{option.label}</span>
            </li>)}
          renderInput={(params) => <TextField {...params} label={t('tag')} />}
        />
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">{t('status')}</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            label={t('status')}
            onChange={(e) => setSelectedCustomer({ ...selectedCustomer as ClientType, status: e.target.value })}
            defaultValue={selectedCustomer?.status}
          >
            <MenuItem value={'ativo'}>{t('active')}</MenuItem>
            <MenuItem value={'inativo'}>{t('inactive')}</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          trackEventMatomo('gestão de clientes', 'click', 'botão', 'salva edição de cliente');
          mutate();
        }}>
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>

  );
}
